// src/client/pages/dashboard.tsx
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@client/components/ui/card';
import { Users, Database, CheckCircle, Activity } from 'lucide-react';
import { ApiClient } from '@client/lib/api-client';
import { queryKeys } from '@client/lib/query-keys';
import type { DashboardPageProps, GetStatsResponse } from '@client/types';

// ============================================================================
// Stats Card Component
// ============================================================================

interface StatCardProps {
  title: string;
  value: number;
  description: string;
  icon: React.ReactNode;
}

function StatCard({ title, value, description, icon }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Loading State Component
// ============================================================================

function DashboardSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <div className="h-9 w-64 bg-gray-200 rounded animate-pulse" />
        <div className="h-4 w-96 bg-gray-100 rounded animate-pulse mt-2" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 w-64 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Error State Component
// ============================================================================

interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
}

function ErrorState({ error, onRetry }: ErrorStateProps) {
  return (
    <div className="p-6">
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-800 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Dashboard Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-700 mb-4">
            Failed to load dashboard data: {error.message}
          </p>
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Main Dashboard Component
// ============================================================================

export default function Dashboard(_props: DashboardPageProps) {
  // Type-safe query with proper query key
  const { 
    data: stats, 
    isLoading, 
    error,
    refetch 
  } = useQuery<GetStatsResponse, Error>({
    queryKey: queryKeys.system.stats(),
    queryFn: () => ApiClient.get<GetStatsResponse>('/stats'),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Loading state
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // Error state
  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  // No data state (shouldn't happen, but type-safe)
  if (!stats) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              No dashboard data available
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main render with data
  return (
    <div className="p-6 space-y-6">
      {/* Welcome Message */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome to TM Application
        </h1>
        <p className="text-muted-foreground mt-1">
          LinkedIn Automation Platform - Dashboard Overview
        </p>
      </div>

      {/* System Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          description="System users"
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />

        <StatCard
          title="Total Lists"
          value={stats.totalLists}
          description="Contact lists"
          icon={<Database className="h-4 w-4 text-muted-foreground" />}
        />

        <StatCard
          title="Total Profiles"
          value={stats.totalProfiles}
          description="LinkedIn profiles"
          icon={<CheckCircle className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      {/* Additional Stats (if available) */}
      {stats.activeUsers !== undefined && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Activity className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeUsers}</div>
              <p className="text-xs text-muted-foreground">
                Currently active
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle>System Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>System is running and operational</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Database connection active</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Authentication working</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity (if available) */}
      {stats.recentActivity && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {stats.recentActivity.lastLogin && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Login:</span>
                  <span className="font-medium">
                    {new Date(stats.recentActivity.lastLogin).toLocaleString()}
                  </span>
                </div>
              )}
              {stats.recentActivity.lastProfileUpdate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Profile Update:</span>
                  <span className="font-medium">
                    {new Date(stats.recentActivity.lastProfileUpdate).toLocaleString()}
                  </span>
                </div>
              )}
              {stats.recentActivity.lastListCreation && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last List Created:</span>
                  <span className="font-medium">
                    {new Date(stats.recentActivity.lastListCreation).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}